import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

// Services
import { ReportService } from '../../services/report.service';
import { AdminService } from '../../services/admin.service';
import { UserService } from '../../services/user.service';
import { LearningService } from '../common/learning/service/learning.service';
import { UpdateService } from '../../modals/report.service';

// Components
import { DislikeListComponent } from './dislike-list/dislike-list.component';

// Models & Constants
import { Report } from '../../models/report.model';
import {
  DAYS_OF_WEEK,
  MONTH_NAMES,
  VISIBILITY_SHOW,
  VISIBILITY_HIDE,
  GROUP_STORAGE_DELAY,
  HTTP_STATUS
} from './my-working-report.constants';

// --- CONSTANTS ---

const STORAGE_KEYS = {
  DEP: 'Dep',
  DATE_FROM: 'ReportDateFrom',
  DATE_TO: 'ReportDateTo',
  VISIBLE: 'visible',
  MODE: 'Drts',
  REPORTS: 'Reports',
  USER: 'User',
  GROUP_NAME: 'groupName',
  REPORT_DATE: 'ReportDate'
};

const CSS_CLASSES = {
  HIDE: 'hide',
  NAVBAR: 'navbar-dark',
  CONTAINER: 'hide-ili-nehide'
};

const TOOLTIPS = {
  WAGE: 'wageRate',
  SUPPLEMENTS: 'salarySupplements'
};

const COLORS = {
  GREEN: 'green',
  GREY: 'grey',
  RED: 'red !important'
};

// --- INTERFACES ---

/**
 * Локальный интерфейс для отчета, используемого в представлении.
 * Не наследуется от глобального Report, чтобы избежать конфликтов типов TS.
 */
interface ReportViewModel {
  userID: string;
  lastName: string;
  howSalaryCalculated: any[];
  summaryRecord: {
    id: string;
    username: string;
    salarySupplements: number;
    wageRate: number;
    [key: string]: any;
  };
  records: {
    workStart: string;
    workEnd: string;
    salarySupplements: number;
    wageRate: number;
    segments: any[];
    [key: string]: any;
  }[];
  [key: string]: any;
}

interface KarmaItem {
  description: string;
  link?: string;
  [key: string]: any;
}

interface CalendarMonth {
  month: string;
  year: number;
  days: number[];
}

interface RowVisibility {
  name: string;
  show: boolean;
}

@Component({
  selector: 'app-my-working-report',
  templateUrl: './my-working-report.component.html',
  styleUrls: ['./my-working-report.component.css']
})
export class MyWorkingReportComponent implements OnInit, OnDestroy {
  public showFilterDropdown = false;
  public isLoaded = false;
  public isRowHidden = false;
  public isEyeVisible = true;
  public visibilityState: string;
  public windowWidth: number;

  public groupName: string;
  public rowNames: RowVisibility[] = [];
  public listKarma: KarmaItem[] = [];
  public salary: number;
  public userId: string; 
  public user: any;
  public group: any;
  
  public totalWeightLevel: number;
  public totalWeightSum: number;
  public totalVacation: number;
  
  public reportDateFrom: string;
  public reportDateTo: string;
  public isGroupMode = false;

  public currentMonth: string;
  public months: CalendarMonth[] = [];
  public workDaysList: string[] = [];
  public daysOfWeek = DAYS_OF_WEEK;
  public monthNames = MONTH_NAMES;

  public isContextMenuVisible = false;
  public isContextMenuOpen = false;
  public contextMenuContent: SafeHtml;
  public contextMenuPosition = { x: 0, y: 0 };
  public currentContextMenuId: string | null = null;

  private subscriptions: Subscription = new Subscription();

  constructor(
    public reportService: ReportService,
    public adminService: AdminService,
    public userService: UserService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private updateService: UpdateService,
    private translate: TranslateService,
    private router: Router,
    private learningService: LearningService
  ) {
    this.initResizeListener();
    this.loadGroupAsync();
  }

  ngOnInit() {
    this.initDateSettings();
    this.initVisibility();
    this.initReportMode();
    this.initUser();
    this.loadReports();
    this.initSubscriptions();
  }

  ngOnDestroy() {
    this.cdr.detach();
    this.toggleGlobalElements(false);
    this.subscriptions.unsubscribe();
  }

  private initResizeListener() {
    this.windowWidth = window.innerWidth;
    window.onresize = () => {
      this.windowWidth = window.innerWidth;
      this.cdr.detectChanges();
    };
  }

  private loadGroupAsync() {
    setTimeout(() => {
      const storedGroup = localStorage.getItem(STORAGE_KEYS.DEP);
      if (storedGroup) {
        this.group = JSON.parse(storedGroup);
      }
    }, GROUP_STORAGE_DELAY);
  }

  private initDateSettings() {
    const now = new Date();
    this.currentMonth = MONTH_NAMES[now.getMonth()];
  }

  private initVisibility() {
    this.visibilityState = localStorage.getItem(STORAGE_KEYS.VISIBLE) || VISIBILITY_SHOW;
    if (!localStorage.getItem(STORAGE_KEYS.VISIBLE)) {
      localStorage.setItem(STORAGE_KEYS.VISIBLE, VISIBILITY_SHOW);
    }
  }

  private initReportMode() {
    this.isGroupMode = localStorage.getItem(STORAGE_KEYS.MODE) === 'group';
    if (!this.isGroupMode) {
      const reports = JSON.parse(localStorage.getItem(STORAGE_KEYS.REPORTS));
      if (reports && reports.length > 0) {
        this.getWorkDays(reports[0].userID);
      }
    }
  }

  private initUser() {
    const routeId = this.route.snapshot.params['id'];
    const storedUser = JSON.parse(localStorage.getItem(STORAGE_KEYS.USER));
    
    this.userId = routeId || (storedUser ? storedUser.id : null);

    if (routeId !== 'group') {
      this.isEyeVisible = false;
      this.fetchUserData();
    }
  }

  private initSubscriptions() {
    this.subscriptions.add(
      this.updateService.update$.subscribe(update => {
        if (update) {
          const reports = JSON.parse(localStorage.getItem(STORAGE_KEYS.REPORTS));
          if (reports && reports.length > 0) {
            this.getWorkDays(reports[0].userID);
          }
          this.updateService.clearNotification();
        }
      })
    );
  }

  private loadReports() {
    this.groupName = localStorage.getItem(STORAGE_KEYS.GROUP_NAME);
    this.group = JSON.parse(localStorage.getItem(STORAGE_KEYS.DEP));
    
    const rawReports = JSON.parse(localStorage.getItem(STORAGE_KEYS.REPORTS)) || [];
    this.reportService.reportsMin = this.normalizeReports(rawReports);
    
    this.rowNames = this.reportService.rowNames;
    this.reportService.allReports = this.reportService.reportsMin.concat(this.reportService.filteredReports);
    this.reportService.reportDate = localStorage.getItem(STORAGE_KEYS.REPORT_DATE);
    
    this.reportService.reportsMin.sort((a: any, b: any) => {
      const nameA = a.lastName || '';
      const nameB = b.lastName || '';
      return nameA.localeCompare(nameB);
    });

    this.isLoaded = true;
  }

  private normalizeReports(reports: ReportViewModel[]): ReportViewModel[] {
    reports.forEach(report => {
      report.summaryRecord.salarySupplements = Number(report.summaryRecord.salarySupplements);
      report.summaryRecord.wageRate = Number(report.summaryRecord.wageRate);
      
      this.reportService.rowNames.push({ 
        name: report.summaryRecord.username, 
        show: true 
      });

      report.records.forEach(record => {
        record.salarySupplements = Number(record.salarySupplements);
        record.wageRate = Number(record.wageRate);
      });
    });
    return reports;
  }

  getTitle(record: any): string {
    const start = record.workStart || 'N/A';
    const end = record.workEnd || 'N/A';
    const segments = record.segments
      .map((s: any) => `Work: ${s.workDays} / Rest: ${s.restDays}`)
      .join(' | ');
    return `Start work: ${start} - End work: ${end}  Period: ${segments}`;
  }

  getTooltipContent(report: ReportViewModel, type: string): SafeHtml {
    let content = '';
    const calc = report.howSalaryCalculated;

    if (type === TOOLTIPS.WAGE) {
      content = this.buildWageTooltip(calc);
    } else if (type === TOOLTIPS.SUPPLEMENTS) {
      content = this.buildSupplementsTooltip(calc, report.summaryRecord.salarySupplements);
    }

    return this.sanitizer.bypassSecurityTrustHtml(content);
  }

  private buildWageTooltip(calc: any[]): string {
    const t = (key: string) => this.translate.instant(key);
    return `
      <span style='color: ${COLORS.RED}'>${t('C_B_P')} ${calc[0]} ${t('RUB')} ${t('V_S_O')} ${calc[1]} ${t('HOUR')}</span><br><br>
      ${calc[0]} ${t('USER_PAY_RATE')}<br>
      / ${calc[1]} ${t('TOTAL_WORKING_HOURS_PER_MONTH')}<br>
      * ${calc[2]} ${t('ACTUAL_TIME_WORKED')}<br>
      = ${calc[4]} ${t('TOTAL_AMOUNT')}
    `;
  }

  private buildSupplementsTooltip(calc: any[], total: number): string {
    const t = (key: string) => this.translate.instant(key);
    const val2 = Number(calc[2]);
    const val3 = Number(calc[3]);
    
    const styleActual = calc[3] !== null ? this.getColorStyle(val2, val3) : '';
    const styleAll = calc[3] !== null ? this.getColorStyle(val3, val2) : '';

    let html = `
      <span style='color: ${COLORS.RED}'>${t('C_B_P')} ${calc[0]} ${t('RUB')} ${t('V_S_O')} ${calc[1]} ${t('HOUR')}</span><br><br>
      ${calc[0]} ${t('USER_PAY_RATE')}<br>
      / ${calc[1]} ${t('TOTAL_WORKING_HOURS_PER_MONTH')}<br>
      * <span style="color: ${styleActual}">${calc[2]} ${t('ACTUAL_TIME_WORKED')}</span>
    `;

    if (calc[3] !== null) {
      html += `&nbsp;|&nbsp;<span style="color: ${styleAll}">${calc[3]} ${t('ALL_TIME')}</span>`;
    }

    html += `<br>
      ${calc[5]} ${t('PUNISHMENT_FOR_NOT_WORKING_TIME')}<br>
      ${calc[6]} ${t('PUNISHMENT_FOR_BEING_LATE')}<br>
      ${calc[7]} ${t('PUNISHMENT_OR_REWARD_FOR_KARMA')}<br>
      ${calc[8]} ${t('R_F_S_L')}<br>
      ${calc[9]} ${t('R_F_T')}<br>
      ${calc[10]} ${t('R_F_L')}<br>
      ${calc[11]} ${t('R_F_LS')}<br>
      = ${total} ${t('TOTAL_AMOUNT')}
    `;
    return html;
  }

  private getColorStyle(val1: number, val2: number): string {
    return val1 >= val2 ? COLORS.GREEN : COLORS.GREY;
  }

  onMouseEnter(event: MouseEvent, report: ReportViewModel, type: string) {
    if (!this.isContextMenuOpen) {
      this.showContextMenu(event, report, type);
    }
  }

  showContextMenu(event: MouseEvent, report: ReportViewModel, type: string) {
    this.contextMenuContent = this.getTooltipContent(report, type);
    this.contextMenuPosition.x = event.clientX - 550;
    this.contextMenuPosition.y = event.clientY - 100;
    this.isContextMenuVisible = true;
    this.isContextMenuOpen = true;
    this.currentContextMenuId = report.summaryRecord.id;
  }

  onMouseLeave() {
    this.hideContextMenu();
  }

  hideContextMenu() {
    this.isContextMenuVisible = false;
    this.isContextMenuOpen = false;
  }

  generateDaysInMonth() {
    const fromStr = localStorage.getItem(STORAGE_KEYS.DATE_FROM);
    const toStr = localStorage.getItem(STORAGE_KEYS.DATE_TO);

    if (!fromStr || !toStr) return;

    const fromDate = new Date(fromStr);
    const toDate = new Date(toStr);

    if (fromDate > toDate) return;

    this.months = [];
    let current = new Date(fromDate.getFullYear(), fromDate.getMonth(), 1);
    const endYear = toDate.getFullYear();
    const endMonth = toDate.getMonth();

    while (
      current.getFullYear() < endYear || 
      (current.getFullYear() === endYear && current.getMonth() <= endMonth)
    ) {
      this.months.push(this.createMonthStructure(current.getFullYear(), current.getMonth()));
      current.setMonth(current.getMonth() + 1);
    }
  }

  private createMonthStructure(year: number, month: number): CalendarMonth {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days: number[] = [];

    let startDay = new Date(year, month, 1).getDay();
    startDay = (startDay === 0) ? 7 : startDay;
    const padding = startDay - 1;

    for (let i = 0; i < padding; i++) days.push(0);
    for (let i = 1; i <= daysInMonth; i++) days.push(i);

    return {
      month: MONTH_NAMES[month],
      year: year,
      days: days
    };
  }

  isWorkingDay(day: number, monthName: string, year: number): boolean {
    if (day === 0) return false;
    const monthIndex = MONTH_NAMES.indexOf(monthName) + 1;
    const dateStr = `${year}-${monthIndex.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    return this.workDaysList.includes(dateStr);
  }

  getWorkDays(id: string) {
    const from = localStorage.getItem(STORAGE_KEYS.DATE_FROM);
    const to = localStorage.getItem(STORAGE_KEYS.DATE_TO);

    this.adminService.getWorkDays(id, from, to).subscribe(
      (response: any) => {
        this.workDaysList = response.body || [];
        this.generateDaysInMonth();
      },
      error => console.error('Error fetching work days', error)
    );
  }

  private fetchUserData() {
    this.adminService.getChtoto(this.userId).subscribe(
      (response: any) => {
        this.user = response.body;
        this.salary = response.body.salary;
        this.fetchSpecializations(this.userId);
        this.fetchVacation(this.userId);
      },
      error => console.error('Error fetching user data:', error)
    );
  }

  private fetchSpecializations(id: string) {
    this.learningService.getLearningSpecs(id).subscribe(
      (response: any) => {
        if (response.status === HTTP_STATUS.OK) {
          this.totalWeightLevel = response.body.sumLevel;
          this.totalWeightSum = response.body.sumWeight;
        }
      },
      error => {
        if (error.status === HTTP_STATUS.BAD_GATEWAY || error.status === HTTP_STATUS.INTERNAL_SERVER_ERROR) {
          this.learningService.getSpecializationsError = true;
          this.isLoaded = true;
        }
      }
    );
  }

  private fetchVacation(id: string) {
    this.userService.getVocation(id).subscribe(
      (response: any) => {
        this.totalVacation = response.body;
      },
      error => console.error('Error fetching vacation data:', error)
    );
  }

  getKarmaInfo(id: string, from?: string, to?: string) {
    const fromDate = from ? from.substring(0, 10) : localStorage.getItem(STORAGE_KEYS.DATE_FROM);
    const toDate = to ? to.substring(0, 10) : localStorage.getItem(STORAGE_KEYS.DATE_TO);

    this.adminService.getKarmaInfo(id, fromDate, toDate).subscribe(
      (response: any) => {
        this.listKarma = this.formatKarmaLinks(response.body || []);
        this.toggleGlobalElements(true);
      },
      error => console.error('Error fetching karma info:', error)
    );
  }

  private formatKarmaLinks(list: any[]): KarmaItem[] {
    return list.map(item => {
      if (item.description && item.description.includes('http')) {
        const parts = item.description.split('http');
        return {
          ...item,
          description: parts[0],
          link: 'http' + parts[1]
        };
      }
      return item;
    });
  }

  getReportDateFrom(): string {
    this.reportDateFrom = localStorage.getItem(STORAGE_KEYS.DATE_FROM);
    return this.reportDateFrom;
  }

  getReportDateTo(): string {
    this.reportDateTo = localStorage.getItem(STORAGE_KEYS.DATE_TO);
    return this.reportDateTo;
  }

  getMonthName(monthIndex: number): string {
    return MONTH_NAMES[monthIndex];
  }

  toggleVisibility() {
    this.visibilityState = this.visibilityState === VISIBILITY_SHOW ? VISIBILITY_HIDE : VISIBILITY_SHOW;
    localStorage.setItem(STORAGE_KEYS.VISIBLE, this.visibilityState);
  }

  rowIsHidden(name: string): boolean {
    const row = this.rowNames.find(x => x.name === name);
    return row ? row.show : false;
  }

  showHideInfo(name: string) {
    const row = this.rowNames.find(x => x.name === name);
    if (row) {
      row.show = !row.show;
    }
  }

  showDropdown() {
    this.showFilterDropdown = !this.showFilterDropdown;
  }

  setEyeVisibility(visible: boolean) {
    this.isEyeVisible = visible;
  }

  toggleRows() {
    this.isRowHidden = !this.isRowHidden;
  }

  closeKarmaPanel() {
    this.listKarma = [];
    this.toggleGlobalElements(false);
  }

  private toggleGlobalElements(hide: boolean) {
    const container = document.getElementsByClassName(CSS_CLASSES.CONTAINER);
    const navbar = document.getElementsByClassName(CSS_CLASSES.NAVBAR);

    if (container.length > 0) {
      hide ? container[0].classList.add(CSS_CLASSES.HIDE) : container[0].classList.remove(CSS_CLASSES.HIDE);
    }
    if (navbar.length > 0) {
      hide ? navbar[0].classList.add(CSS_CLASSES.HIDE) : navbar[0].classList.remove(CSS_CLASSES.HIDE);
    }
  }

  openNewActionModal(list: any) {
    const modalRef = this.modalService.open(DislikeListComponent);
    modalRef.componentInstance.action(list);
  }

  filter(report: Report) {
    this.reportService.filter(report);
  }

  getExcel() {
    this.isLoaded = false;
    this.adminService.getExcelReport(this.reportService.reportsMin, this.groupName).subscribe(
      (response: any) => this.downloadExcel(response),
      error => {
        console.error('Error generating Excel:', error);
        this.isLoaded = true;
      },
      () => this.isLoaded = true
    );
  }

  private downloadExcel(response: any) {
    const blob = new Blob([response.body], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = response.headers.get('filename') || 'report.xlsx';
    link.click();
    window.URL.revokeObjectURL(url);
    link.remove();
  }
}


// WEBPACK FOOTER //
// C:/work/new2/internal-frontend/src/app/content-page/my-working-report/my-working-report.component.ts